import React, { useEffect, useMemo, useState } from 'react';

import InputText from '../../../components/agv-forms-input';
import { LOGO_ACCEPTED_EXTENSIONS, LOGO_MAX_SIZE_MB } from '../client-defaults';
import { FileDropZone, FormSection, Panel } from '../clients-ui';

export const isSiretValid = (siret) =>
  !siret || /^\d{14}$/.test(siret.replace(/\s/g, ''));

export const isInformationStepValid = (information) =>
  information.name.trim().length > 0 && isSiretValid(information.siret);

const Toggle = ({ checked, onChange, label, description }) => (
  <label className="tw-flex tw-cursor-pointer tw-items-start tw-gap-2">
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="tw-mt-0.5 tw-size-4 tw-cursor-pointer tw-accent-main"
    />
    <span className="tw-flex tw-flex-col">
      <span className="tw-text-[13px] tw-text-black">{label}</span>
      <span className="tw-text-[11px] tw-text-[#76808c]">{description}</span>
    </span>
  </label>
);

/**
 * Données du client, communications et logo. Les usines / dépôts ne sont pas
 * gérés ici : le client les crée lui-même depuis ses écrans (Entrepôts).
 * `children` reçoit les blocs propres à un client existant (VROOM).
 */
export default function InformationStep({
  information,
  setInformation,
  logoUrl,
  logoFile,
  setLogoFile,
  isLogoUploading,
  isEdition,
  children,
}) {
  // Pas d'erreur « champ vide » sur le nom avant qu'il ait été saisi.
  const [isNameTouched, setIsNameTouched] = useState(
    information.name.length > 0
  );

  const update = (key) => (value) => {
    if (key === 'name') setIsNameTouched(true);
    setInformation((previous) => ({ ...previous, [key]: value }));
  };

  const logoPreviewUrl = useMemo(
    () => (logoFile ? URL.createObjectURL(logoFile) : null),
    [logoFile]
  );

  useEffect(
    () => () => logoPreviewUrl && URL.revokeObjectURL(logoPreviewUrl),
    [logoPreviewUrl]
  );

  const displayedLogo = logoPreviewUrl || logoUrl;

  return (
    <Panel>
      <FormSection
        title="Identité"
        description="Raison sociale et identifiants, repris sur les factures et les documents générés."
      >
        <InputText
          value={information.name}
          setValue={update('name')}
          fieldTitle="Nom du client"
          isRequired
          emptyError={
            isNameTouched
              ? 'Le champ "Nom du client" ne peut être vide.'
              : undefined
          }
          placeholder="Coopérative du Val"
        />
        <div className="tw-grid tw-grid-cols-2 tw-gap-4 mq750:tw-grid-cols-1">
          <InputText
            value={information.siret}
            setValue={update('siret')}
            fieldTitle="SIRET"
            placeholder="123 456 789 00012"
            isBadFormat={!isSiretValid(information.siret)}
          />
          <InputText
            value={information.tva}
            setValue={update('tva')}
            fieldTitle="N° de TVA intracommunautaire"
            placeholder="FR12345678901"
          />
        </div>
      </FormSection>

      <FormSection
        title="Adresse"
        description="Adresse du siège, affichée sur les factures."
      >
        <InputText
          value={information.street}
          setValue={update('street')}
          fieldTitle="Adresse"
          placeholder="12 rue des Moulins"
        />
        <div className="tw-grid tw-grid-cols-[120px_1fr_1fr] tw-gap-4 mq750:tw-grid-cols-1">
          <InputText
            value={information.zipcode}
            setValue={update('zipcode')}
            fieldTitle="Code postal"
            placeholder="51100"
          />
          <InputText
            value={information.city}
            setValue={update('city')}
            fieldTitle="Ville"
            placeholder="Reims"
          />
          <InputText
            value={information.country}
            setValue={update('country')}
            fieldTitle="Pays"
            placeholder="France"
          />
        </div>
      </FormSection>

      <FormSection
        title="Communications"
        description="Envois faits au nom du client. Sans emails, les administrateurs ne reçoivent pas d’invitation : leur lien d’inscription s’affiche dans la section Administrateurs."
      >
        <Toggle
          checked={information.is_email_sending_enabled}
          onChange={update('is_email_sending_enabled')}
          label="Envoi d’emails activé"
          description="Notifications aux destinataires et invitations des utilisateurs."
        />
        <Toggle
          checked={information.is_sms_sending_enabled}
          onChange={update('is_sms_sending_enabled')}
          label="Envoi de SMS activé"
          description="SMS facturés au client selon la tarification."
        />
      </FormSection>

      <FormSection
        title="Logo"
        description={
          isEdition
            ? 'Affiché dans le back-office et sur les documents. Enregistré dès le dépôt, tracé dans l’historique.'
            : 'Affiché dans le back-office et sur les documents. Envoyé à la création du client.'
        }
      >
        <div className="tw-flex tw-items-stretch tw-gap-4 mq750:tw-flex-col">
          <div className="tw-flex tw-h-[120px] tw-w-[200px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[6px] tw-border tw-border-solid tw-border-[#e6e8ec] tw-bg-[rgba(118,128,140,0.04)] mq750:tw-w-full">
            {displayedLogo ? (
              <img
                src={displayedLogo}
                alt="Logo du client"
                className="tw-max-h-[96px] tw-max-w-[176px] tw-object-contain"
              />
            ) : (
              <span className="tw-text-[11px] tw-text-[#76808c]">
                Aucun logo
              </span>
            )}
          </div>
          <div className="tw-flex-1">
            <FileDropZone
              accept={LOGO_ACCEPTED_EXTENSIONS}
              maxSizeMb={LOGO_MAX_SIZE_MB}
              onFile={setLogoFile}
              disabled={isLogoUploading}
            />
          </div>
        </div>
        {logoFile && !isEdition && (
          <p className="tw-m-0 tw-text-[11px] tw-text-[#76808c]">
            {logoFile.name} sera envoyé à la création du client.
          </p>
        )}
      </FormSection>

      {children}
    </Panel>
  );
}
